import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  forwardRef,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import {
  MatChipInputEvent,
  MatChipsModule,
} from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { BehaviorSubject, Observable, combineLatest, defer, map, startWith } from 'rxjs';

@Component({
  selector: 'app-multi-select-chips',
  templateUrl: './multi-select-chips.component.html',
  styleUrls: ['./multi-select-chips.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectChipsComponent),
      multi: true,
    },
  ],
})
export class MultiSelectChipsComponent implements ControlValueAccessor, OnDestroy {
  @Input() suggestions: string[] = [];
  @Input() placeholder = '';
  @Input() label = '';
  @Input() showLabel = true;
  @Input() required = false;
  @Input() isInvalid = false;
  @Input() invalidFeedback = '';
  @Input() name = '';
  @Input() disabled = false;
  @Input() allowCustomValues = true;
  @Input() addOnBlur = true;
  @Input() caseInsensitiveDedup = true;

  @Output() valueChange = new EventEmitter<string[]>();
  /**
   * Fires once focus has fully settled outside the component (autocomplete
   * panel and chip-input clicks do not trigger it). Use this for save-on-blur
   * patterns where per-chip persistence would be too noisy.
   */
  @Output() chipsBlur = new EventEmitter<string[]>();

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger?: MatAutocompleteTrigger;

  values: string[] = [];
  inputCtrl = new FormControl<string>({ value: '', disabled: false });

  private readonly valuesChanged$ = new BehaviorSubject<void>(undefined);

  filteredOptions$: Observable<string[]> = defer(() =>
    combineLatest([
      this.inputCtrl.valueChanges.pipe(startWith(this.inputCtrl.value ?? '')),
      this.valuesChanged$,
    ]).pipe(map(([value]) => this.filter(value)))
  );

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);

  private blurTimeout: ReturnType<typeof setTimeout> | null = null;

  private onChange: (value: string[]) => void = () => {
    /* noop until Angular wires it via registerOnChange */
  };
  private onTouched: () => void = () => {
    /* noop until Angular wires it via registerOnTouched */
  };

  writeValue(value: string[] | string | null | undefined): void {
    if (Array.isArray(value)) {
      this.values = [...value];
    } else if (typeof value === 'string' && value.length > 0) {
      this.values = [value];
    } else {
      this.values = [];
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.inputCtrl.disable({ emitEvent: false });
    } else {
      this.inputCtrl.enable({ emitEvent: false });
    }
    this.cdr.markForCheck();
  }

  addFromInput(event: MatChipInputEvent): void {
    if (!this.allowCustomValues) {
      event.chipInput?.clear();
      this.inputCtrl.setValue('');
      return;
    }
    const raw = (event.value ?? '').trim();
    if (raw.length === 0) {
      return;
    }
    if (this.tryAdd(raw)) {
      event.chipInput?.clear();
      this.inputCtrl.setValue('');
    }
  }

  addFromAutocomplete(event: MatAutocompleteSelectedEvent): void {
    const value = (event.option.viewValue ?? '').trim();
    if (value.length === 0) {
      return;
    }
    this.tryAdd(value);
    this.inputCtrl.setValue('');
  }

  remove(value: string): void {
    if (this.disabled) {
      return;
    }
    const index = this.values.indexOf(value);
    if (index < 0) {
      return;
    }
    this.values = [...this.values.slice(0, index), ...this.values.slice(index + 1)];
    this.emit();
  }

  openPanel(): void {
    if (this.disabled) {
      return;
    }
    this.autocompleteTrigger?.openPanel();
  }

  markAsTouched(): void {
    this.onTouched();
  }

  ngOnDestroy(): void {
    if (this.blurTimeout !== null) {
      clearTimeout(this.blurTimeout);
      this.blurTimeout = null;
    }
  }

  /**
   * Defer the blur emission so a focusin within the same tick (chip remove,
   * autocomplete option click, chevron click) cancels it. After the wait,
   * if focus has truly left the field (and is not inside a Material overlay),
   * emit chipsBlur once with the current values.
   */
  @HostListener('focusout')
  onHostFocusOut(): void {
    if (this.blurTimeout !== null) {
      clearTimeout(this.blurTimeout);
    }
    this.blurTimeout = setTimeout(() => {
      this.blurTimeout = null;
      const active = (typeof document !== 'undefined' ? document.activeElement : null) as HTMLElement | null;
      const insideHost = !!(active && this.elementRef.nativeElement.contains(active));
      const insideOverlay = !!(active && active.closest('.cdk-overlay-container'));
      if (insideHost || insideOverlay) {
        return;
      }
      this.chipsBlur.emit([...this.values]);
    }, 100);
  }

  @HostListener('focusin')
  onHostFocusIn(): void {
    if (this.blurTimeout !== null) {
      clearTimeout(this.blurTimeout);
      this.blurTimeout = null;
    }
  }

  private tryAdd(value: string): boolean {
    if (this.isAlreadySelected(value)) {
      return false;
    }
    this.values = [...this.values, value];
    this.emit();
    return true;
  }

  private isAlreadySelected(value: string): boolean {
    if (this.caseInsensitiveDedup) {
      const lower = value.toLowerCase();
      return this.values.some((v) => v.toLowerCase() === lower);
    }
    return this.values.includes(value);
  }

  private filter(value: string | null): string[] {
    const filterValue = (value ?? '').toString().toLowerCase();
    return this.suggestions.filter((option) => {
      const matches = option.toLowerCase().includes(filterValue);
      const notSelected = !this.isAlreadySelected(option);
      return matches && notSelected;
    });
  }

  private emit(): void {
    this.onChange(this.values);
    this.valueChange.emit(this.values);
    this.valuesChanged$.next();
    this.cdr.markForCheck();
  }
}
